import type { DashboardLayout, DashboardWidget, WidgetType } from '@sea-snag/shared';
import { Store } from '@tanstack/store';

import { DEFAULT_LAYOUT, WIDGET_REGISTRY } from '../components/dashboard/widget-registry';

// ── State ────────────────────────────────────────────────────────────

interface DashboardStoreState {
  layout: DashboardLayout;
  isEditMode: boolean;
  isCatalogOpen: boolean;
  isDirty: boolean;
  /** Increments on every layout mutation; used to retrigger debounced saves. */
  revision: number;
}

export const dashboardStore = new Store<DashboardStoreState>({
  layout: { widgets: [] },
  isEditMode: false,
  isCatalogOpen: false,
  isDirty: false,
  revision: 0,
});

// ── Actions ──────────────────────────────────────────────────────────

export function setLayout(layout: DashboardLayout): void {
  dashboardStore.setState((prev) => ({ ...prev, layout, isDirty: false, revision: 0 }));
}

export function markSaved(): void {
  dashboardStore.setState((prev) => ({ ...prev, isDirty: false }));
}

export function toggleEditMode(): void {
  dashboardStore.setState((prev) => ({
    ...prev,
    isEditMode: !prev.isEditMode,
    isCatalogOpen: false,
  }));
}

export function openCatalog(): void {
  dashboardStore.setState((prev) => ({ ...prev, isCatalogOpen: true }));
}

export function closeCatalog(): void {
  dashboardStore.setState((prev) => ({ ...prev, isCatalogOpen: false }));
}

export function addWidget(type: WidgetType): void {
  const registration = WIDGET_REGISTRY[type];
  if (!registration) return;

  const newWidget: DashboardWidget = {
    id: crypto.randomUUID(),
    type,
    position: dashboardStore.state.layout.widgets.length,
    config: { ...registration.defaultConfig },
  };

  dashboardStore.setState((prev) => ({
    ...prev,
    layout: {
      widgets: [...prev.layout.widgets, newWidget],
    },
    isDirty: true,
    revision: prev.revision + 1,
  }));
}

export function removeWidget(widgetId: string): void {
  dashboardStore.setState((prev) => {
    const filtered = prev.layout.widgets
      .filter((w) => w.id !== widgetId)
      .map((w, i) => ({ ...w, position: i }));

    return {
      ...prev,
      layout: { widgets: filtered },
      isDirty: true,
      revision: prev.revision + 1,
    };
  });
}

export function reorderWidgets(activeId: string, overId: string): void {
  dashboardStore.setState((prev) => {
    const widgets = [...prev.layout.widgets].sort((a, b) => a.position - b.position);
    const oldIndex = widgets.findIndex((w) => w.id === activeId);
    const newIndex = widgets.findIndex((w) => w.id === overId);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;

    const [moved] = widgets.splice(oldIndex, 1);
    widgets.splice(newIndex, 0, moved!);

    const reordered = widgets.map((w, i) => ({ ...w, position: i }));

    return {
      ...prev,
      layout: { widgets: reordered },
      isDirty: true,
      revision: prev.revision + 1,
    };
  });
}

export function updateWidgetConfig(widgetId: string, config: Record<string, unknown>): void {
  dashboardStore.setState((prev) => ({
    ...prev,
    layout: {
      widgets: prev.layout.widgets.map((w) =>
        w.id === widgetId ? { ...w, config: { ...w.config, ...config } } : w
      ),
    },
    isDirty: true,
    revision: prev.revision + 1,
  }));
}

export function resetToDefault(): void {
  // Generate fresh UUIDs for default layout
  const widgets = DEFAULT_LAYOUT.widgets.map((w, i) => ({
    ...w,
    id: crypto.randomUUID(),
    position: i,
  }));

  dashboardStore.setState((prev) => ({
    ...prev,
    layout: { widgets },
    isDirty: true,
    revision: prev.revision + 1,
  }));
}
